import React, { forwardRef } from 'react';
import Type from 'prop-types';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { CustomerOption, Filters, FilterSection } from 'components';
import { searchService } from 'config/ioc';
import { fieldTypes } from 'constants/form';
import { useAggregateQueries, useTranslation } from 'hooks';
import {
  getIdsOrValuesFromCollection,
  getOptionsByIds,
} from 'utils/formatters';
import { fields } from './CascadeListFilters.mock';

const CascadeListFilters = forwardRef((props, formRef) => {
  const { filters, open, onApply, ...other } = props;
  const initialValues = {};
  const { t } = useTranslation();

  const getCustomers = async (params) => {
    const response = await searchService.getCustomers(params);

    return response.data.data.items;
  };

  const getTeams = async (params) => {
    const response = await searchService.getTeams(params);

    return response.data.data.items;
  };

  const getBranches = async (params) => {
    const response = await searchService.getBranches(params);

    return response.data.data.items;
  };

  const getOffices = async () => {
    const response = await searchService.getOffices();

    return response.data.data.items;
  };

  const getResponsibleGroups = async () => {
    const response = await searchService.getResponsibleGroups();

    return response.data.data.items;
  };

  const filterOptions = createFilterOptions({
    stringify: (option) => `${option.name} ${option.id}`,
  });

  const {
    isSuccess,
    status,
    queries: filterQueries,
  } = useAggregateQueries([
    {
      queryKey: ['cascade_customers', filters.customers],
      queryFn: () => getCustomers({ query: filters.customers }),
      enabled: filters.customers.length > 0,
      initialData: filters.customers.length === 0 ? [] : undefined,
    },
    {
      queryKey: ['cascade_teams', filters.teams],
      queryFn: () => getTeams({ teamId: filters.teams }),
      enabled: filters.teams.length > 0,
      initialData: filters.teams.length === 0 ? [] : undefined,
    },
    { queryKey: 'cascade_branches', queryFn: getBranches, enabled: open },
    { queryKey: 'cascade_offices', queryFn: getOffices, enabled: open },
    {
      queryKey: 'cascade_responsibleGroups',
      queryFn: getResponsibleGroups,
      enabled: open,
    },
  ]);

  const handleSubmit = (formData) => {
    const values = Object.keys(fields).reduce((acc, field) => {
      const value = formData[field];
      const newValue = getIdsOrValuesFromCollection(value);

      return { ...acc, [field]: newValue };
    }, {});
    onApply(values);
  };

  const getInitialValue = (fieldName) => {
    const fieldIdx = fields[fieldName];

    switch (fieldName) {
      case 'withAdvancePayment':
        return filters[fieldName];
      case 'offices':
      case 'branches':
      case 'responsibleGroups':
      case 'customers':
      case 'teams':
        return getOptionsByIds(
          filters[fieldName],
          filterQueries[fieldIdx].data,
        );

      default:
        return null;
    }
  };

  if (isSuccess) {
    Object.keys(fields).forEach((field) => {
      initialValues[field] = getInitialValue(field);
    });
  }

  return (
    <Filters
      ref={formRef}
      initialValues={initialValues}
      open={open}
      onApply={handleSubmit}
      status={status}
      {...other}
    >
      <FilterSection
        name="customers"
        label={t('customer')}
        type={fieldTypes.AUTOCOMPLETE}
        queryConfig={{
          queryKey: ['search', 'customers'],
          queryFn: getCustomers,
        }}
        SearchViewProps={{
          freeSolo: true,
          multiple: false,
          renderOption: (option, state) => {
            return <CustomerOption compact option={option} state={state} />;
          },
        }}
      />

      <FilterSection
        name="teams"
        label={t('team')}
        type={fieldTypes.AUTOCOMPLETE}
        queryConfig={{
          queryKey: ['search', 'teams'],
          queryFn: getTeams,
        }}
        SearchViewProps={{
          freeSolo: true,
        }}
      />

      <FilterSection
        name="offices"
        label={t('dpp')}
        type={fieldTypes.AUTOCOMPLETE}
        options={filterQueries[fields.offices].data}
        SearchViewProps={{
          filterOptions,
        }}
      />

      <FilterSection
        name="branches"
        label={t('branch')}
        type={fieldTypes.AUTOCOMPLETE}
        options={filterQueries[fields.branches].data}
        SearchViewProps={{
          filterOptions,
        }}
      />

      <FilterSection
        name="responsibleGroups"
        label={t('responsibleGroup')}
        type={fieldTypes.AUTOCOMPLETE}
        options={filterQueries[fields.responsibleGroups].data}
        SearchViewProps={{
          filterOptions,
        }}
      />

      <FilterSection
        name="withAdvancePayment"
        label={t('withAdvancePayment')}
        type={fieldTypes.SWITCH}
        showDivider={false}
      />
    </Filters>
  );
});

CascadeListFilters.propTypes = {
  open: Type.bool.isRequired,
  filters: Type.shape({
    customers: Type.arrayOf(Type.string),
    teams: Type.arrayOf(Type.string),
    branches: Type.arrayOf(Type.string),
    offices: Type.arrayOf(Type.string),
    responsibleGroups: Type.arrayOf(Type.string),
    withAdvancePayment: Type.bool,
  }).isRequired,
  onApply: Type.func.isRequired,
};

export default CascadeListFilters;
